// src/components/drivers/DriverForm.jsx
import { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import IconX from "../icons/IconX";

const defaultDriver = {
  id: null, // Firebase Auth UID — required on create, read-only on edit
  name: "",
  email: "",
  phone: "",
  licensePlate: "",
  notes: "",
  disabled: false,
};

/**
 * Driver Form Modal
 *
 * UI for adding + editing drivers. UID is the Firebase Auth UID — admin
 * creates the Auth user in Firebase Console first, copies the UID into
 * this form on create. UID is read-only on edit (changing it would orphan
 * the linkage to the Auth user).
 *
 * @param {Object}        props
 * @param {boolean}       props.isOpen
 * @param {Function}      props.onClose
 * @param {Object|null}   props.driver  — existing driver to edit; null for new
 * @param {Function}      props.onSave  — async (driverData) => void
 */
const DriverForm = ({ isOpen, onClose, driver, onSave }) => {
  const [formData, setFormData] = useState({ ...defaultDriver });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!(driver && driver.id);

  useEffect(() => {
    if (!isOpen) return;
    if (driver) {
      setFormData({
        ...defaultDriver,
        ...driver,
      });
    } else {
      setFormData({ ...defaultDriver });
    }
    setErrors({});
  }, [isOpen, driver]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!isEditing && !formData.id.trim()) {
      next.id = "Firebase Auth UID is required (copy from Console)";
    }
    if (!formData.name.trim()) {
      next.name = "Name is required";
    } else if (formData.name.length > 120) {
      next.name = "Name must be 120 characters or less";
    }
    if (!formData.email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = "Email looks invalid";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      // Surface server-side errors (e.g. duplicate UID) on the form.
      setErrors((prev) => ({ ...prev, _form: err.message || String(err) }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-white rounded-lg shadow-xl">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {isEditing ? "Edit fitter" : "Add fitter"}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <IconX className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                  {/* UID */}
                  <div>
                    <label
                      htmlFor="driver-uid"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Firebase Auth UID
                      {!isEditing && (
                        <span className="text-red-500 ml-0.5">*</span>
                      )}
                    </label>
                    <input
                      id="driver-uid"
                      name="id"
                      type="text"
                      value={formData.id || ""}
                      onChange={handleChange}
                      disabled={isEditing}
                      placeholder="paste from Firebase Console → Authentication → Users"
                      className={`w-full px-3 py-2 border rounded-md font-mono text-sm
                        ${errors.id ? "border-red-400" : "border-gray-300"}
                        ${isEditing ? "bg-gray-100 text-gray-500" : "bg-white"}`}
                    />
                    {errors.id && (
                      <p className="mt-1 text-sm text-red-600">{errors.id}</p>
                    )}
                    {!isEditing && (
                      <p className="mt-1 text-xs text-gray-500">
                        Create the fitter's Auth user in Firebase Console
                        first (Email + Password), then paste their UID here.
                      </p>
                    )}
                  </div>

                  {/* Name */}
                  <div>
                    <label
                      htmlFor="driver-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Name<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <input
                      id="driver-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.name ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="driver-email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <input
                      id="driver-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.email ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Should match the email on the Auth user. Source of truth
                      is Firebase Auth; this is for display only.
                    </p>
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="driver-phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone
                    </label>
                    <input
                      id="driver-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  {/* License plate */}
                  <div>
                    <label
                      htmlFor="driver-licensePlate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Vehicle / licence plate
                    </label>
                    <input
                      id="driver-licensePlate"
                      name="licensePlate"
                      type="text"
                      value={formData.licensePlate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label
                      htmlFor="driver-notes"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Notes
                    </label>
                    <textarea
                      id="driver-notes"
                      name="notes"
                      rows={3}
                      value={formData.notes}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  {errors._form && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      {errors._form}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {isSubmitting
                        ? "Saving…"
                        : isEditing
                        ? "Save changes"
                        : "Add fitter"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DriverForm;
